import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAllProducts, getBestsellers, getNewArrivals } from '../services/productService';
import { getBrands } from '../services/brandService';
import { useMarket } from '../context/MarketContext';
import { toHandle } from '../utils/handle';
import ProductGrid from '../components/product/ProductGrid';
import OfferBanner from '../components/offers/OfferBanner';
import ReviewHighlights from '../components/reviews/ReviewHighlights';
import { typeKey, typeGroups, searchText, hasSkinTypes } from '../utils/catalog';
import {
  BanknotesIcon, LockClosedIcon, MapPinIcon, ShieldCheckIcon, TruckIcon, ArrowRightIcon,
} from '@heroicons/react/24/outline';

/* ── Data ── */
// Category pills and tiles come from the store's own product types (utils/catalog.js): fixed lists such as
// "Serum" or "Body Wash" pointed at types the store does not use and opened empty pages.

const CONCERNS = [
  { label: 'Acne & Breakouts', query: 'acne' },
  { label: 'Pigmentation',     query: 'pigmentation' },
  { label: 'Dryness',          query: 'dry' },
  { label: 'Anti-Aging',       query: 'anti-aging' },
  { label: 'Sensitivity',      query: 'sensitive' },
  { label: 'Dark Circles',     query: 'eye' },
  { label: 'Oily Skin',        query: 'oily' },
  { label: 'Hair Fall',        query: 'hair' },
];

/** Small spaced label + serif heading — the same rhythm on every section */
const SectionHeading = ({ eyebrow, title, subtitle, align = 'center' }) => (
  <div className={`mb-8 md:mb-10 ${align === 'center' ? 'text-center' : ''}`}>
    {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
    <h2 className="display-title text-[1.7rem] md:text-4xl">{title}</h2>
    {subtitle && <p className="text-ink-soft mt-2 text-sm md:text-base">{subtitle}</p>}
  </div>
);

/** A soft tile around a real product photo (hero collage) */
const PhotoCard = ({ product, className = '' }) => (
  <Link to={`/product/${product.handle}`}
    className={`group block bg-white rounded-3xl border border-[#EDEAF2] shadow-[0_20px_50px_-24px_rgba(59,7,100,0.35)] overflow-hidden ${className}`}>
    <div className="bg-[#F6F4F9] aspect-square flex items-center justify-center">
      <img src={product.images[0]} alt={product.title} loading="eager"
        className="w-full h-full object-contain p-5 mix-blend-multiply group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="px-3.5 py-2.5">
      <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-ink-muted">{product.vendor}</p>
      <p className="text-xs font-medium text-ink truncate">{product.title}</p>
    </div>
  </Link>
);

const HomePage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [bestsellers,    setBestsellers]     = useState([]);
  const [newArrivals,    setNewArrivals]     = useState([]);
  const [brands,         setBrands]          = useState([]);
  const [catalogue,      setCatalogue]       = useState([]);
  const navigate = useNavigate();
  const { currentMarket } = useMarket();
  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;

  useEffect(() => {
    getBestsellers(20, marketCode).then(setBestsellers).catch(() => {});
    getNewArrivals(8, marketCode).then(setNewArrivals).catch(() => {});
    getBrands().then(setBrands).catch(() => {});
    getAllProducts({ marketCode }).then(setCatalogue).catch(() => {});
  }, [marketCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The brands the store really sells, biggest first (the hero used to name brands it does not stock)
  const topBrands = useMemo(() => [...brands].sort((a, b) => (b.productCount ?? 0) - (a.productCount ?? 0)).map(b => b.name), [brands]);
  const brandLine = topBrands.length > 3 ? `${topBrands.slice(0, 3).join(', ')} & more`
    : topBrands.length ? topBrands.join(', ').replace(/, ([^,]*)$/, ' & $1') : 'trusted derma brands';

  // Hero first-slide sub text — shows market-appropriate free-shipping threshold
  const heroFirstSub = currentMarket?.isDefault
    ? `Authentic dermatologist-recommended products from ${brandLine}. Free shipping on prepaid ₹${currentMarket?.freeShippingThreshold ?? 699}+.`
    : `Authentic dermatologist-recommended products from ${brandLine}. Free shipping on orders ${currentMarket?.currencySymbol}${currentMarket?.freeShippingThreshold ?? 99}+.`;

  // Real numbers only: what the catalogue holds today
  const heroStats = [
    catalogue.length >= 10 ? [`${Math.floor(catalogue.length / 10) * 10}+`, 'Products'] : catalogue.length ? [String(catalogue.length), 'Products'] : null,
    brands.length ? [String(brands.length), brands.length === 1 ? 'Brand' : 'Brands'] : null,
    ['100%', 'Authentic'],
  ].filter(Boolean);

  const topBrandCards = useMemo(() => [...brands].sort((a, b) => (b.productCount ?? 0) - (a.productCount ?? 0)).slice(0, 8), [brands]);
  const shopCategories = useMemo(() => typeGroups(catalogue).slice(0, 8), [catalogue]);
  // skin-concern tiles that would open an empty page are left out; each shows a real matching product
  const concerns = useMemo(() => {
    const texts = catalogue.map(searchText);
    return CONCERNS.map(c => {
      const hits = catalogue.filter((p, i) => texts[i].includes(c.query));
      return { ...c, count: hits.length, image: hits.find(p => p.images?.[0])?.images[0] };
    }).filter(c => c.count > 0);
  }, [catalogue]);
  // hero collage: three real product photos (the newest first)
  const heroProducts = useMemo(() => {
    const pool = [...newArrivals, ...bestsellers, ...catalogue];
    const seen = new Set();
    return pool.filter(p => p.images?.[0] && !seen.has(p.id) && seen.add(p.id)).slice(0, 3);
  }, [newArrivals, bestsellers, catalogue]);
  const skinTypesKnown = useMemo(() => hasSkinTypes(catalogue), [catalogue]);
  // The API has no sales ranking yet (its "bestseller" list is simply the newest products), so this section is
  // called "Featured" and leaves out the four already shown under New Arrivals — no product twice on one page.
  const featured = useMemo(() => {
    const shownAsNew = new Set(newArrivals.slice(0, 4).map(p => p.id));
    const rest = bestsellers.filter(p => !shownAsNew.has(p.id));
    return rest.length >= 4 ? rest : bestsellers;
  }, [bestsellers, newArrivals]);
  // featured tabs: only types in that list, so no tab is ever empty
  const bestsellerTabs = useMemo(() => typeGroups(featured).slice(0, 6), [featured]);

  // Trust strip — COD is India-only; other items are universal
  const trustItems = [
    ...(currentMarket?.isDefault ? [{ Icon: BanknotesIcon, title: 'Cash on Delivery', sub: 'Pan-India' }] : []),
    { Icon: LockClosedIcon,  title: 'Secured Shopping',  sub: '256-bit SSL' },
    { Icon: MapPinIcon,      title: 'Live Tracking',     sub: 'Real-time' },
    { Icon: ShieldCheckIcon, title: '100% Authentic',    sub: 'Verified & sealed' },
    { Icon: TruckIcon,       title: 'Fast Delivery',     sub: currentMarket?.isDefault ? '2–4 business days' : '5–7 business days' },
  ];

  const filteredProducts = activeCategory === 'all'
    ? featured
    : featured.filter(p => typeKey(p) === activeCategory);


  return (
    <>
      <Helmet>
        <title>Skinn Thrive — Authentic Derma Skincare Online India</title>
        <meta name="description" content="Shop authentic dermatologist-recommended skincare, haircare & sunscreen from trusted derma brands. Free shipping on prepaid orders ₹699+." />
        <link rel="canonical" href={process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Skinn Thrive" />
        <meta property="og:title" content="Skinn Thrive — Authentic Derma Skincare Online India" />
        <meta property="og:description" content="Shop authentic dermatologist-recommended skincare, haircare & sunscreen from trusted derma brands. Free shipping on prepaid orders ₹699+." />
        <meta property="og:url" content={process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'} />
        <meta property="og:image" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Skinn Thrive — Authentic Derma Skincare Online India" />
        <meta name="twitter:description" content="Shop authentic dermatologist-recommended skincare, haircare & sunscreen from trusted derma brands." />
        <meta name="twitter:image" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/og-image.jpg`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Skinn Thrive',
          url: process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/search?q={search_term_string}` },
            'query-input': 'required name=search_term_string',
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Skinn Thrive',
          url: process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com',
          logo: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/logo192.png`,
          sameAs: [],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'info@skinnthrive.com',
            availableLanguage: 'en',
          },
        })}</script>
      </Helmet>

      {/* ═══════════════════ HERO ═══════════════════ */}
      {/* Light and calm, with the store's own product photos (it was a purple slider with a stock makeup photo) */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F7F4FC] via-[#FBFAFD] to-white">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-60 blur-3xl pointer-events-none"
             style={{ background: 'radial-gradient(circle, #E9DDFB, transparent 70%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 md:pt-16 md:pb-20">
          <div className="grid md:grid-cols-[1.05fr_1fr] gap-8 md:gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white border border-primary/15 text-primary text-[10px] md:text-[11px] font-semibold tracking-[0.08em] px-3 md:px-3.5 py-1.5 rounded-full mb-5 md:mb-6 shadow-sm">
                <ShieldCheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                100% Authentic · Dermatologist Approved
              </span>

              <h1 className="display-title text-[2.15rem] leading-[1.08] md:text-6xl lg:text-[4.2rem] mb-4 md:mb-5">
                Skin Science,<br />
                <span className="italic text-primary">Not Skincare Hype.</span>
              </h1>

              <p className="text-ink-soft text-[15px] md:text-lg max-w-lg mb-6 md:mb-8 leading-relaxed">
                {heroFirstSub}
              </p>

              <div className="flex gap-2.5 md:gap-3">
                <Link to="/shop" className="btn-primary h-11 px-5 text-sm md:h-[48px] md:px-8 md:text-base shadow-lg shadow-primary/20 whitespace-nowrap">
                  Shop All Products <ArrowRightIcon className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link to="/collections/new-arrivals" className="btn-outline h-11 px-5 text-sm md:h-[48px] md:px-8 md:text-base bg-white/60 whitespace-nowrap">
                  New Arrivals
                </Link>
              </div>

              {heroStats.length > 0 && (
                <div className="flex items-center gap-5 md:gap-8 mt-7 md:mt-9">
                  {heroStats.map(([n, l], i) => (
                    <div key={l} className={`${i > 0 ? 'pl-5 md:pl-8 border-l border-primary/15' : ''}`}>
                      <p className="display-title text-xl md:text-3xl">{n}</p>
                      <p className="text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-ink-muted mt-0.5">{l}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Real product photos */}
            <div className="relative">
              {heroProducts.length === 3 ? (
                <div className="grid grid-cols-3 md:grid-cols-2 gap-3 md:gap-5 md:max-w-[520px] md:ml-auto">
                  <PhotoCard product={heroProducts[0]} className="md:row-span-2 md:self-center md:translate-y-0" />
                  <PhotoCard product={heroProducts[1]} className="md:-translate-y-4" />
                  <PhotoCard product={heroProducts[2]} className="md:translate-y-4" />
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-2 gap-3 md:gap-5 md:max-w-[520px] md:ml-auto" aria-hidden="true">
                  {[0, 1, 2].map(i => <div key={i} className={`rounded-3xl shimmer aspect-[4/5] ${i === 0 ? 'md:row-span-2 md:self-center' : ''}`} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ TRUST STRIP ═══════════════════ */}
      <div className="bg-white border-y border-[#EDEAF2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className={`grid gap-4 md:gap-0 ${trustItems.length === 5 ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
            {trustItems.map(({ Icon, ...b }, i) => (
              <div key={b.title} className={`flex items-center gap-3 md:justify-center md:px-4 ${i > 0 ? 'md:border-l border-[#EDEAF2]' : ''}`}>
                <span className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                  <Icon className="w-[18px] h-[18px] text-primary" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-ink">{b.title}</p>
                  <p className="text-[11px] text-ink-muted">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <OfferBanner />

      {/* ═══════════════════ SHOP BY CATEGORY ═══════════════════ */}
      {shopCategories.length > 0 && <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <SectionHeading eyebrow="Browse" title="Shop by Category" subtitle="Everything your skin & hair needs" />
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {shopCategories.map(cat => (
            <Link
              key={cat.key}
              to={`/shop?type=${encodeURIComponent(cat.label)}`}
              className="group flex flex-col items-center gap-2.5 p-2.5 md:p-3 rounded-2xl border border-border bg-white
                          hover:border-primary/30 hover:-translate-y-1 hover:shadow-card-hover
                          transition-all duration-200 text-center"
            >
              <div className="w-full aspect-square rounded-xl bg-primary-50 overflow-hidden flex items-center justify-center">
                {cat.image ? (
                  <img src={cat.image} alt="" loading="lazy"
                    className="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <span className="text-lg font-bold text-primary">{cat.label.slice(0, 1)}</span>
                )}
              </div>
              <span className="text-[10px] md:text-[11px] font-semibold text-ink group-hover:text-primary transition-colors leading-tight">
                {cat.label}
                <span className="block text-ink-muted font-medium mt-0.5">{cat.count} product{cat.count !== 1 ? 's' : ''}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>}

      {/* ═══════════════════ FEATURED ═══════════════════ */}
      <section className="bg-surface border-y border-border py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <SectionHeading eyebrow="Our range" title="Featured Products" subtitle="Authentic derma essentials from trusted brands" align="left" />
            <Link to="/shop" className="btn-outline btn-sm hidden md:inline-flex shrink-0 -mt-4">
              View All →
            </Link>
          </div>

          {/* Category filter tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-7 scrollbar-hide -mx-1 px-1">
            {[{ key: 'all', label: 'All' }, ...bestsellerTabs].map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 border
                  ${activeCategory === cat.key
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-white text-ink-soft border-gray-200 hover:border-primary/40 hover:text-primary'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <ProductGrid products={filteredProducts.slice(0, 8)} />
          <div className="text-center mt-8 md:hidden">
            <Link to="/shop" className="btn-outline">View All Products →</Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ SHOP BY CONCERN ═══════════════════ */}
      {concerns.length > 0 && <section className="py-12 md:py-20 bg-[#F8F6FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Targeted care" title="Shop by Concern" subtitle="Find the right product for what you want to treat" />
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {concerns.map(c => (
              <Link
                key={c.label}
                to={`/shop?q=${c.query}`}
                className="group w-[calc(33.333%-0.5rem)] sm:w-36 md:w-40 flex flex-col bg-white rounded-2xl border border-[#EDEAF2] overflow-hidden
                           hover:border-primary/30 hover:shadow-card-hover transition-all duration-200 text-center"
              >
                <div className="bg-[#F6F4F9] aspect-square flex items-center justify-center">
                  {c.image && (
                    <img src={c.image} alt="" loading="lazy"
                      className="w-full h-full object-contain p-4 mix-blend-multiply group-hover:scale-105 transition-transform duration-300" />
                  )}
                </div>
                <div className="px-2 py-2.5">
                  <p className="text-[11px] md:text-xs font-semibold text-ink group-hover:text-primary transition-colors leading-tight">{c.label}</p>
                  <p className="text-[10px] text-ink-muted mt-0.5">{c.count} product{c.count !== 1 ? 's' : ''}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>}

      {/* ═══════════════════ NEW ARRIVALS ═══════════════════ */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="flex items-end justify-between">
            <SectionHeading eyebrow="Just in" title="New Arrivals" subtitle="The latest additions to our shelves" align="left" />
            <Link to="/collections/new-arrivals" className="btn-outline btn-sm hidden md:inline-flex shrink-0 mb-10">
              See All <ArrowRightIcon className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          </div>
          <ProductGrid products={newArrivals.slice(0, 4)} />
          <div className="text-center mt-8 md:hidden">
            <Link to="/collections/new-arrivals" className="btn-outline">See All New Arrivals</Link>
          </div>
        </section>
      )}

      {/* ═══════════════════ SHOP BY BRAND ═══════════════════ */}
      {brands.length > 0 && <section className="bg-[#FBFAFD] border-y border-[#EDEAF2] py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <SectionHeading eyebrow="Trusted names" title="Shop by Brand" subtitle="100% authentic · direct from authorized distributors" align="left" />
            <Link to="/brands" className="btn-outline btn-sm hidden md:inline-flex shrink-0 mb-10">
              All Brands <ArrowRightIcon className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          </div>
          {/* name cards — the old "ABB / AJA" initials looked unfinished */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {topBrandCards.map((brand) => (
              <Link
                key={brand.id}
                to={`/collections/${brand.slug || toHandle(brand.name)}`}
                className="group flex items-center justify-between gap-3 p-4 md:p-5 bg-white rounded-2xl border border-[#EDEAF2]
                           hover:border-primary/30 hover:shadow-card-hover transition-all duration-200"
              >
                <div className="min-w-0">
                  <p className="font-display text-lg md:text-xl font-semibold text-ink truncate group-hover:text-primary transition-colors">{brand.name}</p>
                  {brand.productCount > 0 && (
                    <p className="text-[11px] text-ink-muted mt-0.5">{brand.productCount} product{brand.productCount !== 1 ? 's' : ''}</p>
                  )}
                </div>
                <ArrowRightIcon className="w-4 h-4 text-ink-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-6 md:hidden">
            <Link to="/brands" className="btn-outline btn-sm">View All Brands</Link>
          </div>
        </div>
      </section>}

      {/* ═══════════════════ CUSTOMER REVIEWS ═══════════════════ */}
      {/* real, approved reviews only — hidden until the store publishes them */}
      <ReviewHighlights renderSection={(cards) => (
        <section className="py-14 md:py-20" style={{ background: 'linear-gradient(135deg, #3B0764 0%, #5B21B6 55%, #7C3AED 100%)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="display-title text-white text-[1.7rem] md:text-4xl mb-1">From our customers</h2>
              <p className="text-white/70 text-sm">What shoppers told us about their orders</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{cards}</div>
          </div>
        </section>
      )} />

      {/* ═══════════════════ CTA BANNER ═══════════════════ */}
      {/* skin-type shortcuts only work once products have skin types filled in */}
      {skinTypesKnown && <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div
          className="relative rounded-2xl overflow-hidden px-8 md:px-16 py-12 text-center"
          style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-30 blur-3xl"
               style={{ background: 'radial-gradient(circle, #C026D3, transparent)' }} />
          <div className="relative">
            <p className="eyebrow mb-3">Find Your Match</p>
            <h2 className="display-title text-[1.7rem] md:text-4xl mb-3">
              Not sure what's right for your skin?
            </h2>
            <p className="text-ink-soft mb-7 max-w-lg mx-auto text-sm md:text-base">
              Browse by your skin type and discover dermatologist-curated products tailored for you.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {['Oily','Dry','Sensitive','Combination'].map(st => (
                <button key={st} onClick={() => navigate(`/shop?skinType=${st}`)} className="btn-primary">
                  {st} Skin
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>}
    </>
  );
};

export default HomePage;
